import React, { useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';

import GridItem from 'components/Grid/GridItem';
import Accordion from 'components/Accordion/Accordion';
import GridContainer from 'components/Grid/GridContainer';
import { HeadingMBold } from 'components/Typography/Typography';

import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/components/paymentMethodsStyle';

import { HelpOutlineOutlined } from '@material-ui/icons';

import { upIdSelector } from 'redux/modules/session/selectors';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { getPaymentMethodLabels } from 'redux/modules/dynamicLabels/selectors';

import { translate } from 'components/Translation';
import { setDataLayer } from 'commons/gtm/dataLayerWrapper';

import { PayWith, PaymentMethodProps, PaymentStatusType } from './type';
import { PaymentMethodProvider, usePaymentMethod } from './context';

import { getLanguage } from 'utils/language/language';
import { isBOMIntroOn, isBOMOn } from 'utils/killswitch';
import { BOM_INTRO_POPUP, PAYMENT_METHOD_MAPPED } from './constant';
import { getSessionData, setSessionData } from './helper/session';

import {
	AccordionContent,
	AccordionTitle,
	CardPayment,
	OrderSummary,
	PaymentStatus,
	BOMPayment,
	DeleteCardConfirmation,
} from './subcomponents';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';

// @ts-ignore
const useStyles = makeStyles(styles);

const PaymentMethod = ({
	bills = [],
	onPaymentResult,
	paymentPreference,
	referrer,
}: Omit<PaymentMethodProps, 'onCardDelete'>) => {
	const classes = useStyles();

	// kill switch to check if Bank Muscat are to be shown
	const remoteConfig = useSelector(remoteConfigSelector);
	const isBankMuscatEnabled = isBOMOn(remoteConfig);
	const isBankMuscatIntroEnabled = isBOMIntroOn(remoteConfig);

	const paymentMethodLabels = useSelector(getPaymentMethodLabels);
	const accId = useSelector(upIdSelector);
	const {
		tokens: { BOM },
		tokenLoading,
		defaultMethod,
	} = usePaymentMethod();

	const handleClickOpen = (event: React.MouseEvent<HTMLDivElement>) => {
		event.stopPropagation();
		event.preventDefault();
		setDataLayer('info_click', getLanguage(), null, accId, null, {
			infoType: 'Pay with Bank Muscat',
		});
		setDataLayer('info_view', getLanguage(), null, accId, null, {
			infoType: 'Bank Muscat Tutorial',
			firstOpen: '1',
		});
	};

	const defaultPaymentMethod: PayWith = useMemo(() => {
		// if bank muscat disabled then Card is default maypment
		if (!isBankMuscatEnabled) {
			return PayWith.CARD;
		}

		if (PAYMENT_METHOD_MAPPED.includes(paymentPreference as PayWith)) {
			return paymentPreference as PayWith;
		}

		return defaultMethod ?? PayWith.CARD;
	}, [isBankMuscatEnabled, paymentPreference, defaultMethod]);

	useEffect(() => {
		// Check whether Bank Muscat intro popup is open (stored in session storage)
		const isBankPopupOpen = getSessionData(BOM_INTRO_POPUP, undefined);

		// Check conditions for showing the intro popup
		if (defaultPaymentMethod === PayWith.CARD || BOM.tokens.length > 0 || isBankPopupOpen) {
			return;
		}

		// Update session storage to indicate that the intro popup is open
		setSessionData(BOM_INTRO_POPUP, 1);
	}, [defaultPaymentMethod]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	if (!bills.length) {
		return <PaymentStatus status={PaymentStatusType.ERROR} />;
	}

	if (tokenLoading) {
		return <CustomCircularProgress text={translate('loading.it.can.take.a.while')} loading transparent />;
	}

	return (
		<GridContainer>
			<GridContainer>
				<GridItem lg={12} xs={12} md={12} sm={12}>
					<HeadingMBold color="secondary-light">{paymentMethodLabels?.paymentMethod}</HeadingMBold>
				</GridItem>
			</GridContainer>
			<GridContainer>
				<GridItem xs={12} sm={12} lg={6} md={6}>
					<Accordion
						key={defaultMethod}
						active={[PAYMENT_METHOD_MAPPED.indexOf(defaultPaymentMethod)]}
						collapses={[
							{
								id: 'credit_card',
								title: <AccordionTitle title={paymentMethodLabels?.paymentsMethodCreditDebit} />,
								content: (
									<AccordionContent>
										<CardPayment
											bills={bills}
											onPaymentResult={onPaymentResult}
											referrer={referrer}
											paymentPreference={defaultPaymentMethod}
										/>
									</AccordionContent>
								),
							},
							isBankMuscatEnabled && {
								id: 'bank_muscat',
								title: <AccordionTitle title={paymentMethodLabels?.paymentsMethodBankMuscat} />,
								content: (
									<AccordionContent>
										<BOMPayment
											bills={bills}
											onPaymentResult={onPaymentResult}
											referrer={referrer}
											paymentPreference={defaultPaymentMethod}
										/>
									</AccordionContent>
								),
							},
						]}
					/>
					<DeleteCardConfirmation />
				</GridItem>
				<GridItem xs={12} lg={6} md={6}>
					<OrderSummary bills={bills} />
				</GridItem>
			</GridContainer>
		</GridContainer>
	);
};

export default (props: Omit<PaymentMethodProps, 'onCardDelete'>) => (
	<PaymentMethodProvider>
		<PaymentMethod {...props} />
	</PaymentMethodProvider>
);
